import React from 'react'
import { Grid, Box } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import messages from '@src/routes/Configurators/messages'
import { FormattedMessage } from 'react-intl'
import { DefaultValue } from '../../BiTube/enum'
import styles from '@src/routes/Configurators/utils/styles'
import useFormikHelper from '../useFormikHelper'

const useStyles = makeStyles((theme: Theme) => ({
  label: {
    fontWeight: 'bold',
    paddingTop: theme.spacing(2),
  },
  seqWrap: {
    paddingLeft: theme.spacing(3.5),
    paddingTop: theme.spacing(2),
    width: theme.spacing(210),
    wordBreak: 'break-all',
  },
  sequencePreview: {
    paddingLeft: theme.spacing(3.5),
  },
  lineHeight: {
    lineHeight: theme.spacing(0.5),
  },
  flex: {
    display: 'flex',
    '& > div': {
      flexBasis: 'fit-content',
    },
  },
  noShrink: {
    flexShrink: 0,
  },
  sequenceItem: {
    width: 12,
  },
  sequenceItemWithPhos: {
    width: 15,
  },
  sequenceItemWithBase: {
    width: 33,
  },
  sequenceItemWithBaseAndPhos: {
    width: 36,
  },
  antiSeqSpacer: {
    width: 18,
  },
  subscript: {
    bottom: theme.spacing(-2.5),
    position: 'relative',
    paddingBottom: 5,
  },
  flexRowContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
}))

const MiRNASequencePreview = () => {
  const classes = useStyles()
  const GlobalCSS = styles()

  const formik = useFormikHelper()
  const {
    fiveMod,
    sequence,
    threeMod,
    antisenceThreeMod,
    antisenceSequence,
    customerOverhang,
  } = formik.selectedOligoValues

  const FIRST_BREAK_INDEX = 3

  const generateAntiSeqArray = (): string[] => {
    const seq = antisenceSequence?.split('')
    const seqArray: string[] = []
    const seqWithPattern: string[] = []

    if (!seq) return []

    for (let i = 0; i < seq.length; i++) {
      if (!seq[i].trim()) continue

      if (seq[i] === '*') {
        const prev = seqArray[seqArray.length - 1]
        seqArray[seqArray.length - 1] = prev + seq[i]
        continue
      }

      if (seqWithPattern.length) {
        seqWithPattern.push(seq[i])
        if (seq[i] === ']') {
          seqArray.push(seqWithPattern.join(''))
          seqWithPattern.length = 0
        }
      } else {
        if (seq[i] === '[') {
          seqWithPattern.push(seq[i])
        } else {
          seqArray.push(seq[i])
        }
      }
    }

    return seqArray.filter(Boolean)
  }

  const getSequenceType = (index: number): string => {
    const antiSeq = generateAntiSeqArray()
    const length = antiSeq[index]?.trim()?.length

    switch (length) {
      case 1:
        return classes.sequenceItem
      case 2:
        return classes.sequenceItemWithPhos
      case 4:
        return classes.sequenceItemWithBase
      case 5:
        return classes.sequenceItemWithBaseAndPhos
      default:
        return ''
    }
  }

  const renderSequenceCharacters = (chars: string[], startIndex = 0) => {
    return chars.map((char, index) => (
      <Box
        key={`${index}-char`}
        className={getSequenceType(index + startIndex)}
      >
        {char}
      </Box>
    ))
  }

  const sequenceDisplay = (sequenceValue: string) => {
    if (sequenceValue === '--' || sequenceValue.length <= 3) {
      return sequenceValue
    }

    const seq = sequenceValue.split('').reverse().join('')
    const firstPart = seq.slice(0, FIRST_BREAK_INDEX).split('')
    const bottomString = seq.slice(FIRST_BREAK_INDEX, FIRST_BREAK_INDEX + 1)
    const secondPart = seq.slice(FIRST_BREAK_INDEX + 1).split('')

    return (
      <>
        {renderSequenceCharacters(firstPart)}
        <Box component="sub" className={classes.subscript}>
          \
          <Box component="sub" className={GlobalCSS.subToSubscriot}>
            {bottomString}
          </Box>
          /
        </Box>
        {renderSequenceCharacters(secondPart, FIRST_BREAK_INDEX)}
      </>
    )
  }

  const antisenceSequenceDisplay = (
    sequenceValue: string | undefined,
    threemod: string | undefined
  ) => {
    const sequenceArray = generateAntiSeqArray()

    if (sequenceValue === '--' || sequenceArray.length <= 3) {
      return sequenceArray
    }

    const firstPart = sequenceArray.slice(0, FIRST_BREAK_INDEX)
    const secondPart = sequenceArray.slice(FIRST_BREAK_INDEX)

    return (
      <>
        {firstPart.map((item, index) => (
          <Box key={`${index}-firstPart`} className={getSequenceType(index)}>
            {item}
          </Box>
        ))}
        <Box className={classes.antiSeqSpacer} />
        {secondPart.map((item, index) => (
          <Box
            key={`${index}-secondPart`}
            className={getSequenceType(index + FIRST_BREAK_INDEX)}
          >
            {item}
          </Box>
        ))}
        {threemod}
      </>
    )
  }

  const calculatePaddingLength = (): number => {
    if (antisenceSequence === DefaultValue.sequencePreview) return 14
    if (antisenceSequence === '--') return 0
    if (!threeMod) return 48

    const threeModset1 = ['[Phos]', '[ThiC6]', '[Btn]']
    const threeModset2 = ['[Cyanine3]', '[Cyanine5]']

    let modPadding = 0
    if (threeModset1.includes(threeMod)) {
      modPadding = (threeMod.length - 1) * 2 * 4
    } else if (threeModset2.includes(threeMod)) {
      modPadding = (threeMod.length - 1.5) * 2 * 4
    } else {
      modPadding = threeMod.length * 2 * 4
    }

    return 48 + modPadding
  }

  const miRNAAntisenseComponent = () => {
    const paddingLength = calculatePaddingLength()
    const isPreview = antisenceSequence === DefaultValue.sequencePreview

    const className = `${classes.flexRowContainer}${
      isPreview
        ? ` ${classes.sequencePreview} ${GlobalCSS.globalHelpTextColor}`
        : ''
    }`

    return (
      <Box sx={{ paddingLeft: paddingLength }} className={className}>
        {isPreview
          ? antisenceSequence
          : antisenceSequenceDisplay(antisenceSequence, antisenceThreeMod)}
      </Box>
    )
  }

  const miRNASenseComponent = () => {
    const isPreview = sequence === DefaultValue.sequencePreview

    const className = `${classes.lineHeight} ${classes.flex}${
      isPreview
        ? ` ${classes.sequencePreview} ${GlobalCSS.globalHelpTextColor}`
        : ''
    }`

    return (
      <Box className={className}>
        <Box className={classes.noShrink}>{threeMod}</Box>
        <Box className={classes.noShrink}>{customerOverhang}</Box>
        <Box className={classes.flexRowContainer}>
          {isPreview ? sequence : sequenceDisplay(sequence)}
          {fiveMod}
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Grid container>
        <Grid item xs={2} className={classes.label}>
          <FormattedMessage {...messages.CONF_MIRNA_AANTISENCE_PROPERTIES} />
        </Grid>
        <Grid item xs={10} className={classes.seqWrap}>
          {miRNAAntisenseComponent()}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={2} className={classes.label}>
          <FormattedMessage {...messages.CONF_MIRNA_SENCE_PROPERTIES} />
        </Grid>
        <Grid item xs={10} className={classes.seqWrap}>
          {miRNASenseComponent()}
        </Grid>
      </Grid>
    </Box>
  )
}

export default MiRNASequencePreview
